/**
 * Best-effort country detection from the visitor's IP.
 *
 * Used only to PRE-SELECT the country on the registration form. It is deliberately
 * incapable of breaking that form:
 *   - the request is fire-and-forget with a short timeout
 *   - any failure resolves to null and the field simply stays empty
 *   - the answer is cached for the tab, so a reload or a step back costs nothing
 *
 * The endpoint is configurable, so this can be pointed at your own server (or switched
 * off entirely) without touching a component:
 *   VUE_APP_GEOIP_URL=https://api.amejpay.com/v1/geoip     -> use your own
 *   VUE_APP_GEOIP_URL=off                                  -> disable detection
 */

const CACHE_KEY = "amej.geoip";
const TIMEOUT_MS = 2500;

/** Literal read - webpack's DefinePlugin only substitutes the exact expression. */
function configuredUrl() {
  const url = process.env.VUE_APP_GEOIP_URL;
  if (url === undefined || url === null || url === "") { return "https://ipapi.co/json/"; }
  return String(url);
}

function readCache() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

function writeCache(value) {
  try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(value)); } catch (e) { /* private mode */ }
}

/** Accepts the shapes of the common providers without committing to one. */
function normalise(d) {
  if (!d || typeof d !== "object") { return null; }
  const name = d.country_name || d.country || d.countryName || "";
  const code = d.country_code || d.countryCode || d.country_code_iso3 || "";
  const dial = d.country_calling_code || d.calling_code || d.dial_code || d.phone_code || "";
  if (!name && !code) { return null; }
  return {
    name: String(name),
    code: String(code).toUpperCase(),
    // Stored WITHOUT the leading "+", the way this API holds dialing codes.
    dial: String(dial).replace(/^\+/, ""),
  };
}

/**
 * Resolves to { name, code, dial } or null. Never rejects.
 */
export function detectCountry() {
  const url = configuredUrl();
  if (url.toLowerCase() === "off" || url.toLowerCase() === "false") {
    return Promise.resolve(null);
  }

  const cached = readCache();
  if (cached) { return Promise.resolve(cached.country || null); }

  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return Promise.resolve(null);
  }

  return new Promise(function (resolve) {
    let settled = false;
    const finish = function (value) {
      if (settled) { return; }
      settled = true;
      writeCache({ country: value, at: Date.now() });
      resolve(value);
    };

    // A hung lookup must not leave the form waiting.
    const timer = setTimeout(function () { finish(null); }, TIMEOUT_MS);

    window.fetch(url, { method: "GET", credentials: "omit" })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (d) { clearTimeout(timer); finish(normalise(d)); })
      .catch(function () { clearTimeout(timer); finish(null); });
  });
}

/**
 * Finds the row in the API's country list that matches a detected country.
 * Name first, then ISO code, then dialing code - each list has different fields.
 */
export function matchCountry(list, detected) {
  if (!detected || !Array.isArray(list) || !list.length) { return null; }
  const name = String(detected.name || "").trim().toLowerCase();
  const code = String(detected.code || "").trim().toLowerCase();
  const dial = String(detected.dial || "").trim();

  const nameOf = function (c) {
    return String(c.name || c.country_name || c.title || c.country || "").trim().toLowerCase();
  };
  const codeOf = function (c) {
    return String(c.code || c.iso_code || c.country_code || c.iso2 || "").trim().toLowerCase();
  };
  const dialOf = function (c) {
    return String(c.country_dialing_code || c.dialing_code || c.phone_code || "").replace(/^\+/, "").trim();
  };

  let hit = list.filter(function (c) { return name && nameOf(c) === name; });
  if (hit.length) { return hit[0]; }
  hit = list.filter(function (c) { return code && codeOf(c) === code; });
  if (hit.length) { return hit[0]; }
  hit = list.filter(function (c) { return dial && dialOf(c) === dial; });
  return hit.length ? hit[0] : null;
}

export default { detectCountry, matchCountry };
